import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { translation, useSelector } from '@hh.ru/front-static-app';

import ContainerForMicroFrontend from 'src/components/CodeInjector/ContainerForMicroFrontend';
import { updateQuestionToAnswerMap } from 'src/models/applicant/resume/blocks/creds/actions';
import { selectResumeCreds } from 'src/models/applicant/resume/blocks/creds/selectors';
import { Creds } from 'src/models/applicant/resume/blocks/creds/types';
import { isAnyQuestionRendered } from 'src/models/applicant/resume/blocks/creds/utils';
import { Permission } from 'src/models/resume/resumeCommon.types';
import ResumeCard from 'src/pages/ResumeView/redesign/common/Card';

import QuestionAnswer from 'src/pages/ResumeView/redesign/blocks/creds/Card/QuestionAnswer';

import styles from './styles.less';

const CREDS_UPDATE = 'rpf-creds-update';
const EDIT_OPEN = 'rpf-creds-edit-open';

declare global {
    interface WindowEventMap {
        [CREDS_UPDATE]: CustomEvent;
    }
}

const TrlKeys = {
    title: 'rb.title.creds',
};

const ApplicantResumeCreds = translation(({ trls }) => {
    const dispatch = useDispatch();
    const creds = useSelector(selectResumeCreds);
    const canEdit = useSelector((state) => state.resume?.permission === Permission.Edit);

    const handleEdit = () => {
        window.dispatchEvent(new CustomEvent(EDIT_OPEN));
    };

    const handleUpdateQuestionToAnswerMap = useCallback(
        (questionToAnswerMap: Record<string, string[]>) => {
            dispatch(updateQuestionToAnswerMap(questionToAnswerMap));
        },
        [dispatch]
    );

    useEffect(() => {
        const handleUpdateCreds = (event: CustomEvent<{ creds: Creds }>) => {
            event.detail.creds && handleUpdateQuestionToAnswerMap(event.detail.creds.questionToAnswerMap);
        };

        window.addEventListener(CREDS_UPDATE, handleUpdateCreds);

        return () => window.removeEventListener(CREDS_UPDATE, handleUpdateCreds);
    }, [handleUpdateQuestionToAnswerMap]);

    if (!creds) {
        return null;
    }

    const anyQuestionRendered = isAnyQuestionRendered(creds);

    if (!anyQuestionRendered && !canEdit) {
        return null;
    }

    return (
        <>
            <ResumeCard label={trls[TrlKeys.title]} onEdit={handleEdit}>
                <div className={styles.answers}>
                    {Object.entries(creds.questionToAnswerMap).map(([questionId, answers]) => (
                        <QuestionAnswer key={questionId} questionId={questionId} answers={answers} />
                    ))}
                </div>
            </ResumeCard>
            <ContainerForMicroFrontend place={'credsEditor'} />
        </>
    );
});

export default ApplicantResumeCreds;
